import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Función universal para descargar PDFs desde el backend
 * Maneja correctamente la autenticación y la descarga del archivo
 * 
 * @param {string} endpoint - Endpoint relativo (ej: "/purchase-requests/123/pdf")
 * @param {string} filename - Nombre del archivo a descargar (ej: "NP-2026-0001.pdf")
 * @param {string} loadingMessage - Mensaje mientras se genera (opcional)
 */
export async function downloadPdf(endpoint, filename, loadingMessage = 'Generando PDF...') {
  const toastId = toast.info(loadingMessage);
  
  try {
    const fullUrl = `${BACKEND_URL}/api${endpoint}`;
    console.log('[downloadPdf] Iniciando descarga:', { endpoint, filename, url: fullUrl });
    
    const response = await axios({
      method: 'GET',
      url: fullUrl,
      withCredentials: true,
      responseType: 'blob',
      timeout: 60000,
    });
    
    console.log('[downloadPdf] Respuesta recibida:', { 
      status: response.status, 
      size: response.data?.size,
      contentType: response.headers['content-type']
    });
    
    // Verificar que la respuesta sea válida
    if (!response.data || response.data.size === 0) {
      toast.dismiss(toastId);
      toast.error('El PDF está vacío');
      return false;
    }
    
    // Verificar que sea un PDF y no un error JSON
    const respContentType = response.headers['content-type'];
    if (respContentType && respContentType.includes('application/json')) {
      const text = await response.data.text();
      console.error('[downloadPdf] Error JSON recibido:', text);
      toast.dismiss(toastId);
      try {
        const error = JSON.parse(text);
        toast.error(error.detail || 'Error al generar archivo');
      } catch {
        toast.error('Error al generar archivo');
      }
      return false;
    }
    
    // Crear blob con tipo correcto (detectar del response)
    const blobType = respContentType || 'application/octet-stream';
    const blob = new Blob([response.data], { type: blobType });
    const finalFilename = filename;
    
    console.log('[downloadPdf] Blob creado:', { blobSize: blob.size });
    
    // Método para descargar el blob
    const downloadBlob = () => {
      // Método 1: msSaveBlob para IE/Edge legacy
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, finalFilename);
        return;
      }
      
      // Método 2: Crear URL y anchor element
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = finalFilename;
      
      // Hacer el link visible momentáneamente para mejor compatibilidad
      link.style.cssText = 'position:fixed;left:0;top:0;opacity:0;pointer-events:none;';
      document.body.appendChild(link);
      
      // Click con un pequeño delay
      requestAnimationFrame(() => {
        link.click();
        
        // Limpiar después
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 10000);
      });
    };
    
    downloadBlob();
    
    toast.dismiss(toastId);
    toast.success('Archivo descargado');
    return true;
    
  } catch (error) {
    console.error('[downloadPdf] Error completo:', error);
    toast.dismiss(toastId);
    
    if (error.response) {
      const status = error.response.status;
      if (status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else if (status === 404) {
        toast.error('El documento no existe');
      } else if (status === 403) {
        toast.error('No tienes permiso para descargar este archivo');
      } else {
        // Intentar leer el error del blob
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          toast.error(parsed.detail || 'Error al descargar archivo');
        } catch {
          toast.error('Error al descargar archivo');
        }
      }
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Tiempo de espera agotado. Intenta nuevamente.');
    } else {
      toast.error('Error al descargar archivo');
    }
    
    return false;
  }
}

/**
 * Función para descargar un PDF POST (como los reportes)
 */
export async function downloadPdfPost(endpoint, data, filename, loadingMessage = 'Generando reporte...') {
  const toastId = toast.info(loadingMessage);
  
  try {
    const response = await axios({
      method: 'POST',
      url: `${BACKEND_URL}/api${endpoint}`,
      data: data,
      withCredentials: true,
      responseType: 'blob',
      timeout: 60000,
    });
    
    if (!response.data || response.data.size === 0) {
      toast.dismiss(toastId);
      toast.error('El reporte está vacío');
      return false;
    }
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const finalFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    
    // Método para descargar el blob
    const downloadBlob = () => {
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, finalFilename);
        return;
      }
      
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = finalFilename;
      link.style.cssText = 'position:fixed;left:0;top:0;opacity:0;pointer-events:none;';
      document.body.appendChild(link);
      
      requestAnimationFrame(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 10000);
      });
    };
    
    downloadBlob();
    
    toast.dismiss(toastId);
    toast.success('Reporte descargado');
    return true;
    
  } catch (error) {
    console.error('[downloadPdfPost] Error:', error);
    toast.dismiss(toastId);
    if (error.response) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        toast.error(parsed.detail || 'Error al generar reporte');
      } catch {
        toast.error(error.response.status === 403 ? 'No tienes permiso' : 'Error al generar reporte');
      }
    } else {
      toast.error('Error al generar reporte');
    }
    return false;
  }
}

/**
 * Función para descargar CSV/Excel desde el backend
 */
export async function downloadCsv(endpoint, filename, loadingMessage = 'Generando archivo...') {
  const toastId = toast.info(loadingMessage);
  
  try {
    const response = await axios({
      method: 'GET',
      url: `${BACKEND_URL}/api${endpoint}`,
      withCredentials: true,
      responseType: 'blob',
      timeout: 60000,
    });
    
    if (!response.data || response.data.size === 0) {
      toast.dismiss(toastId);
      toast.error('El archivo está vacío');
      return false;
    }
    
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const finalFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    link.style.cssText = 'position:fixed;left:0;top:0;opacity:0;pointer-events:none;';
    document.body.appendChild(link);
    
    requestAnimationFrame(() => {
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 10000);
    });
    
    toast.dismiss(toastId);
    toast.success('Archivo descargado');
    return true;
    
  } catch (error) {
    console.error('[downloadCsv] Error:', error);
    toast.dismiss(toastId);
    toast.error('Error al descargar archivo');
    return false;
  }
}
